import React from "react";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { useRouter } from "next/router";
import { ChatIcon } from "@heroicons/react/solid";
import { usePreviousImmediate } from "rooks";
import { Button, Image, classNames } from "@gonoodle/gn-universe-ui";
import { concatenateQueryParams } from "@gonoodle/gn-universe-utils";
import {
  SECTIONS,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";

import ChampChatBubble from "./ChampChatBubble";
import ProgressBar from "./ProgressBar";
import { QUERY_KEYS, TRANSMOGRIFIER, ROUTE_PATHS } from "../../constants";
import { useUser } from "../../contexts/user";
import Transmogrifier from "../Transmogrifier";
import { useFullscreen, useTransmogrifierPoints } from "../../hooks";
import api from "../../api";
import { pauseJWPlayer } from "../../utils/jwplayer";
import { useLogEvent } from "../../contexts/Analytics";
import { getUtmParamsFromQueryParams } from "../../utils/analytics";

const JSURL = require("jsurl");

const ACTIONS = {
  RESET: "reset",
  PROMPT_CHAMP_QUOTE: "prompt-champ-quote",
  PROMPT_LEVEL_UP: "prompt-level-up",
  PROMPT_GRADUATE: "prompt-graduate",
  LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT:
    "logged-out-user-prompt-create-account",
  TRANSMOGRIFIER_LEVEL_UP: "transmogrifier-level-up",
  TRANSMOGRIFIER_GRADUATE: "transmogrifier-graduate",
  SHOW_CHAMP_MESSAGE: "show-champ-message",
  HIDE_CHAMP_MESSAGE: "hide-champ-message",
  LOGGED_IN_STATUS_CHANGE: "logged-in-status-change",
};
const OPEN_TRANMOGRIFIER = {
  LEVEL_UP: "LEVEL_UP",
  GRADUATE: "GRADUATE",
};
const CHAMP_MESSAGES = {
  PROMPT_CHAMP_QUOTE: "prompt-champ-quote",
  PROMPT_LEVEL_UP: "prompt-level-up",
  PROMPT_GRADUATE: "prompt-graduate",
  LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT:
    "logged-out-user-prompt-create-account",
};
const client = api();
const POINTS_EARNED_NOTIFICATION_TIMEOUT = 3000;

export default function ChampProgressBar() {
  const { user } = useUser();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isLoggedOutUser = !user.isLoggedIn;
  const isFullscreen = useFullscreen();
  const [pointsEarned, setPointsEarned] = React.useState(0);

  const {
    profile,
    champ,
    points,
    level,
    maxPoints,
    phaseTransitionImages,
    champImages,
  } = useTransmogrifierPoints({
    onPointsEarned: (numPoints) => {
      setPointsEarned(numPoints);
    },
  });

  const { data: champs } = useQuery(QUERY_KEYS.CHAMPS, () =>
    client.getChamps(),
  );

  const { logEvent: logUnregisteredChampAchievementEvent } = useLogEvent({
    event: "Unregistered Champ Achievement",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => {},
    },
  });

  const { logEvent: logReachChampAchievementEvent } = useLogEvent({
    event: "Reach Champ Achievement",
    options: {
      referrer: () => {},
    },
  });

  const { logEvent: logLevelUpVideoEvent } = useLogEvent({
    event: "Level Up Video",
    options: {
      referrer: () => {},
    },
  });

  const { logEvent: logLevelUpVideoSkippedEvent } = useLogEvent({
    event: "Level Up Video Skipped",
    options: {
      referrer: () => {},
    },
  });

  const { logEvent: logNewChampSelectedEvent } = useLogEvent({
    event: "New Champ Selected",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => {},
    },
  });

  const getInitialState = () =>
    champ?.reachedMaxLevel
      ? {
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.PROMPT_GRADUATE,
          openTransmogrifier: null,
        }
      : points >= maxPoints && isLoggedOutUser
      ? {
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT,
          openTransmogrifier: null,
        }
      : points >= maxPoints && !isLoggedOutUser
      ? {
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.PROMPT_LEVEL_UP,
          openTransmogrifier: null,
        }
      : {
          showChampMessage: false,
          champMessage: null,
          openTransmogrifier: null,
        };

  const [state, dispatch] = React.useReducer((prevState, action) => {
    switch (action.type) {
      case ACTIONS.RESET:
        return {
          showChampMessage: false,
          champMessage: null,
          openTransmogrifier: null,
        };

      case ACTIONS.PROMPT_CHAMP_QUOTE:
        return {
          ...prevState,
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.PROMPT_CHAMP_QUOTE,
        };

      case ACTIONS.PROMPT_LEVEL_UP:
        logReachChampAchievementEvent({
          champName: champ?.name,
          level: String(level),
        });
        return {
          ...prevState,
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.PROMPT_LEVEL_UP,
        };

      case ACTIONS.PROMPT_GRADUATE:
        return {
          ...prevState,
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.PROMPT_GRADUATE,
        };
      case ACTIONS.LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT:
        logUnregisteredChampAchievementEvent();
        return {
          ...prevState,
          showChampMessage: true,
          champMessage: CHAMP_MESSAGES.LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT,
        };

      case ACTIONS.SHOW_CHAMP_MESSAGE:
        return { ...prevState, showChampMessage: true };

      case ACTIONS.HIDE_CHAMP_MESSAGE:
        return { ...prevState, showChampMessage: false };

      case ACTIONS.TRANSMOGRIFIER_LEVEL_UP:
        logLevelUpVideoEvent({ newChampLevel: String(level + 1) });
        return {
          ...prevState,
          openTransmogrifier: OPEN_TRANMOGRIFIER.LEVEL_UP,
        };

      case ACTIONS.TRANSMOGRIFIER_GRADUATE:
        logReachChampAchievementEvent({ champName: champ?.name, level: "Max" });
        return {
          ...prevState,
          openTransmogrifier: OPEN_TRANMOGRIFIER.GRADUATE,
        };

      case ACTIONS.LOGGED_IN_STATUS_CHANGE:
        return getInitialState();

      default:
        return prevState;
    }
  }, getInitialState());

  const { mutate: levelUpChamp } = useMutation(client.levelUpChamp);

  const { mutate: chooseNewChamp } = useMutation(client.chooseNewChamp, {
    onSuccess: () => {
      dispatch({ type: ACTIONS.RESET });
      queryClient.invalidateQueries(QUERY_KEYS.USER);
    },
  });

  React.useEffect(() => {
    if (!state.openTransmogrifier) {
      queryClient.invalidateQueries(QUERY_KEYS.USER);
    }
  }, [state.openTransmogrifier, queryClient]);

  // Sync up previous points after 5s and
  // update state of component.
  React.useEffect(() => {
    if (!isFullscreen && pointsEarned) {
      // Ensure any chat bubble is hidden before
      // notifying points earned.
      dispatch({ type: ACTIONS.HIDE_CHAMP_MESSAGE });

      const timeout = setTimeout(() => {
        setPointsEarned(0);

        if (champ?.reachedMaxLevel) {
          dispatch({ type: ACTIONS.PROMPT_GRADUATE });
        } else if (points >= maxPoints) {
          dispatch({
            type: isLoggedOutUser
              ? ACTIONS.LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT
              : ACTIONS.PROMPT_LEVEL_UP,
          });
        } else if (champ?.messages) {
          dispatch({ type: ACTIONS.PROMPT_CHAMP_QUOTE });
        }
      }, POINTS_EARNED_NOTIFICATION_TIMEOUT);

      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [
    points,
    isLoggedOutUser,
    maxPoints,
    pointsEarned,
    isFullscreen,
    champ?.reachedMaxLevel,
    champ?.messages,
  ]);

  const prevIsLoggedOutUser = usePreviousImmediate(isLoggedOutUser);

  React.useEffect(() => {
    if (
      prevIsLoggedOutUser !== null &&
      isLoggedOutUser !== prevIsLoggedOutUser
    ) {
      dispatch({ type: ACTIONS.LOGGED_IN_STATUS_CHANGE });
    }
  });

  if (!user.features.transmogrifier || !champ) {
    return null;
  }

  function getChampQuote() {
    const index = (points - 1) % champ.messages.length;
    return champ.messages[index]?.text;
  }

  const nextLevel = level + 1;

  return (
    <section
      className="sticky bottom-0 order-last z-20 max-sm:mt-5 mt-16"
      aria-label="Champ progress bar"
    >
      {state.openTransmogrifier === OPEN_TRANMOGRIFIER.LEVEL_UP && (
        <Transmogrifier.LevelUp
          champ={champ}
          level={nextLevel}
          isOpen
          onClose={() => dispatch({ type: ACTIONS.RESET })}
        />
      )}

      {state.openTransmogrifier === OPEN_TRANMOGRIFIER.GRADUATE && (
        <Transmogrifier.Graduate
          champ={champ}
          isOpen
          onChampSelected={(id) => {
            const selectedChamp = champs?.find((c) => c.id === id);
            logNewChampSelectedEvent({
              champName: selectedChamp.name,
              prevChampName: champ.name,
            });
            levelUpChamp(null, {
              onSuccess: () => {
                chooseNewChamp(id);
              },
            });
          }}
        />
      )}

      {state.showChampMessage &&
        (state.champMessage === CHAMP_MESSAGES.PROMPT_LEVEL_UP ? (
          <ChampChatBubble
            message={`Nice job! You got all ${maxPoints} points!`}
            imageSources={
              phaseTransitionImages
                ? {
                    "regular@1x": phaseTransitionImages.full["1x"],
                    "regular@2x": phaseTransitionImages.full["2x"],
                    "regular@3x": phaseTransitionImages.full["3x"],
                  }
                : null
            }
          >
            <Button
              className="mt-4 w-full"
              variant="vivid"
              size="md"
              onPress={() => {
                dispatch({ type: ACTIONS.TRANSMOGRIFIER_LEVEL_UP });
                levelUpChamp();
                pauseJWPlayer();
              }}
            >
              Level Up!
            </Button>

            <Button
              className="mt-4 w-full"
              variant="dark-outline"
              size="md"
              onPress={() => {
                logLevelUpVideoSkippedEvent({
                  newChampLevel: String(level + 1),
                });
                dispatch({ type: ACTIONS.RESET });
                levelUpChamp(null, {
                  onSuccess: () =>
                    queryClient.invalidateQueries(QUERY_KEYS.USER),
                });
              }}
            >
              Skip
            </Button>
          </ChampChatBubble>
        ) : state.champMessage === CHAMP_MESSAGES.PROMPT_GRADUATE ? (
          <ChampChatBubble
            message="Congratulations! You've maxed out your Champ"
            imageSources={
              phaseTransitionImages
                ? {
                    "regular@1x": phaseTransitionImages.full["1x"],
                    "regular@2x": phaseTransitionImages.full["2x"],
                    "regular@3x": phaseTransitionImages.full["3x"],
                  }
                : null
            }
          >
            <Button
              className="mt-4 w-full"
              variant="vivid"
              size="md"
              onPress={() => {
                dispatch({ type: ACTIONS.TRANSMOGRIFIER_GRADUATE });
                pauseJWPlayer();
              }}
            >
              Graduate Champ
            </Button>
          </ChampChatBubble>
        ) : state.champMessage === CHAMP_MESSAGES.PROMPT_CHAMP_QUOTE ? (
          <ChampChatBubble
            onDismiss={() => dispatch({ type: ACTIONS.RESET })}
            message={getChampQuote()}
          />
        ) : state.champMessage ===
          CHAMP_MESSAGES.LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT ? (
          <ChampChatBubble
            onDismiss={() => dispatch({ type: ACTIONS.HIDE_CHAMP_MESSAGE })}
            message="Nice job earning points! Create an account to continue growing with your Champ."
            imageSources={
              phaseTransitionImages
                ? {
                    "regular@1x": phaseTransitionImages.full["1x"],
                    "regular@2x": phaseTransitionImages.full["2x"],
                    "regular@3x": phaseTransitionImages.full["3x"],
                  }
                : null
            }
          >
            <Button
              className="mt-4 w-full"
              variant="vivid"
              size="md"
              onPress={() => {
                router.push(
                  concatenateQueryParams(ROUTE_PATHS.REGISTRATION, {
                    referrer: JSURL.stringify({
                      pathname: router.pathname,
                      query: router.query,
                      sourceElement: SECTIONS_TYPES.TRANSMOGRIFIER,
                      sourceName: SECTIONS.CREATE_ACCOUNT_BUTTON,
                    }),
                  }),
                  ROUTE_PATHS.REGISTRATION,
                );
              }}
            >
              Create an Account
            </Button>
          </ChampChatBubble>
        ) : null)}

      <div
        className={classNames(
          "bg-black flex items-stretch",
          "max-sm:flex-col max-sm:items-start",
        )}
      >
        <div className="flex items-center gap-2 shrink-0 pt-4 px-5 py-2 sm:py-2 sm:px-5 relative">
          <div
            className={classNames("relative", pointsEarned >= 1 && "opacity-0")}
          >
            <Image
              className="h-20 max-sm:h-14"
              sources={{
                "regular@1x": champImages.headshot["1x"],
                "regular@2x": champImages.headshot["2x"],
                "regular@3x": champImages.headshot["3x"],
              }}
              alt=""
            />

            {!state.showChampMessage &&
              state.champMessage ===
                CHAMP_MESSAGES.LOGGED_OUT_USER_PROMPT_CREATE_ACCOUNT && (
                <button
                  className="absolute inset-0 flex justify-end group"
                  onClick={() => dispatch({ type: ACTIONS.SHOW_CHAMP_MESSAGE })}
                  aria-haspopup="dialog"
                  aria-controls="chat-bubble"
                  aria-expanded={false}
                >
                  <span className="sr-only">Display message</span>

                  <ChatIcon
                    className="w-6 mr-4 max-sm:mr-1 fill-white transition group-hover:fill-purple-500"
                    aria-hidden
                  />
                </button>
              )}
          </div>
          <div
            className={classNames(
              "flex flex-col shrink-0",
              pointsEarned >= 1 && "opacity-0",
            )}
          >
            <p className="gn-text font-bold text-white">{champ.name}</p>
            <p className="gn-text-sm text-purple-300 max-w-2xl truncate max-sm:max-w-3xl">
              {isLoggedOutUser ? "Level 1" : profile.name}
            </p>
          </div>

          <div
            className={classNames(
              "absolute max-sm:hidden w-full h-[200%] left-0 bg-black",
              pointsEarned < 1
                ? "[transition:opacity_0.1s,top_0.1s,visibility_0s_0.1s] top-0 opacity-0 invisible"
                : "[transition:opacity_0.1s,top_0.1s] -top-full opacity-100 visible",
            )}
          >
            <div
              className={classNames(
                "relative w-full h-full",
                "bg-[radial-gradient(var(--tw-gradient-from)_0,var(--tw-gradient-to)_70%)] from-purple to-transparent",
              )}
            >
              <Image
                className="bg-contain bg-center bg-no-repeat w-full h-full"
                sources={{
                  "regular@1x": champImages.headshot["1x"],
                  "regular@2x": champImages.headshot["2x"],
                  "regular@3x": champImages.headshot["3x"],
                }}
                alt=""
              />
            </div>
          </div>
        </div>

        <div className="w-full py-4 px-5 sm:py-2 sm:px-5 relative flex items-center">
          <div
            className={classNames(
              "absolute left-0 w-full sm:h-full bg-black px-5 py-2 sm:py-2 sm:px-5 flex items-center",
              pointsEarned < 1
                ? "[transition:opacity_0.1s,top_0.1s,visibility_0s_0.1s] top-0 opacity-0 invisible"
                : "[transition:opacity_0.1s,top_0.1s] -top-full opacity-100 visible",
            )}
          >
            <p
              className="text-white font-display text-xl sm:text-2xl xs:mt-4"
              aria-live="polite"
            >
              {champ.name}{" "}
              <span className="text-purple-300">
                earned {pointsEarned} point{pointsEarned > 1 && "s"}!
              </span>
            </p>
          </div>

          <ProgressBar
            current={points}
            max={maxPoints}
            limit={
              isLoggedOutUser ? TRANSMOGRIFIER.LOGGED_OUT_USER_MAX_POINTS : null
            }
            target={`${champ?.reachedMaxLevel ? "Max" : `Lv ${nextLevel}`}`}
          />
        </div>
      </div>
    </section>
  );
}
